import React, { useState } from 'react';

// Highlight overlay: admin places a shape on the slide; during the lesson the
// shape is hidden until the student taps the spot, at which point it fades in.
// Tapping again hides it. In editor mode the shape is always visible with a
// dashed hint border so the admin can position/resize it.
const HighlightElement = ({
  element,
  isEditor = false,
  canInteract = true,
  disabled = false,
  syncedRevealed = null,
  onReveal = null,
}) => {
  const { content = {} } = element;
  const {
    shape = 'underline',
    color = '#e11d48',
    strokeWidth = 4,
  } = content;

  const isControlled = typeof syncedRevealed === 'boolean';
  const [localRevealed, setLocalRevealed] = useState(false);
  const revealed = isControlled ? syncedRevealed : localRevealed;

  const canClick = !isEditor && !disabled && canInteract;

  const handleClick = () => {
    if (!canClick) return;
    const next = !revealed;
    if (isControlled) {
      onReveal?.(next);
    } else {
      setLocalRevealed(next);
      onReveal?.(next);
    }
  };

  const showShape = isEditor || revealed;

  const strokeProps = {
    stroke: color,
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    fill: 'none',
    vectorEffect: 'non-scaling-stroke',
  };

  const renderShape = () => {
    switch (shape) {
      case 'circle':
        // Preserve aspect so a circle stays circular inside a rectangular box.
        return (
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
          >
            <ellipse cx="50" cy="50" rx="46" ry="46" {...strokeProps} />
          </svg>
        );
      case 'oval':
        // Stretch to the container so admins can control shape proportions.
        return (
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <ellipse cx="50" cy="50" rx="47" ry="42" {...strokeProps} />
          </svg>
        );
      case 'cross':
        return (
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
          >
            <line x1="14" y1="14" x2="86" y2="86" {...strokeProps} />
            <line x1="86" y1="14" x2="14" y2="86" {...strokeProps} />
          </svg>
        );
      case 'underline':
      default:
        // Slightly wavy underline for a hand-drawn feel.
        return (
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M 4 70 Q 25 58, 50 68 T 96 66"
              {...strokeProps}
            />
          </svg>
        );
    }
  };

  return (
    <div
      className="w-full h-full relative select-none"
      onClick={handleClick}
      style={{ cursor: canClick ? 'pointer' : 'default' }}
    >
      {isEditor && (
        <div
          className="absolute inset-0 rounded-md pointer-events-none"
          style={{
            border: '2px dashed rgba(0, 0, 0, 0.25)',
          }}
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          opacity: showShape ? 1 : 0,
          transition: 'opacity 300ms ease',
          pointerEvents: 'none',
        }}
      >
        {renderShape()}
      </div>
    </div>
  );
};

export default HighlightElement;
